/**
 * @design-contract CUR-113 + CUR-206
 *
 * Pure selectors over a chat-message array. Encodes the rules for what
 * counts as a "turn", a "cluster", an "active progress slot", the "last
 * text of a turn", and an interleaved turn (CUR-206). Render code MUST
 * consume these instead of hand-rolling `lastIndexOf` / loop arithmetic
 * inline — the chat-display tests assert these specific rules and
 * inlining the math at multiple call sites is how they drift.
 *
 * INSTRUCTIONS FOR HUMAN OR AI AGENTS EDITING THIS FILE:
 *   - All functions here are PURE and SYNCHRONOUS. Do not introduce
 *     React state, hooks, or side effects. The render code uses these
 *     during commit and inside `useMemo` deps; impure behavior here
 *     will cause infinite re-renders.
 *   - Do not loosen any rule to make an unrelated test pass elsewhere.
 *     The rules guard real shipped bugs documented in CUR-113 / CUR-206.
 *   - When adding new selectors, prefer narrow, single-purpose functions
 *     over a single "do everything" selector. Selectors should compose.
 */

import type { ChatMessage } from '../chatTypes'

/**
 * Index of the most recent user message in the array, or -1 if none.
 *
 * Used to anchor the "latest turn" — all assistant messages with index
 * greater than this belong to the in-flight or just-completed turn.
 */
export function getLatestUserMessageIndex(messages: ChatMessage[]): number {
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    if (messages[i].role === 'user') return i
  }
  return -1
}

/**
 * Returns the most recent user message, or null if none. Convenience
 * wrapper around `getLatestUserMessageIndex` for call sites that only
 * need the message itself.
 */
export function getLatestUserMessage(messages: ChatMessage[]): ChatMessage | null {
  const idx = getLatestUserMessageIndex(messages)
  return idx >= 0 ? messages[idx] : null
}

/**
 * Index of the LATEST assistant text message in the latest turn, or -1 if
 * the latest turn has none.
 *
 * CUR-206: with interleaved turns, multiple assistant text messages can
 * coexist in the same turn (segment-per-phase). The "active response" is
 * the LATEST text segment — the one currently streaming (or most recently
 * emitted). Falling back to the first would attach runway / min-height
 * anchors to a long-since-completed segment.
 *
 * Optional timestamp gate: post-stream silent refetches can briefly
 * insert a prior-turn text row above the latest user message. Passing
 * `latestUserCreatedAtMs` + a `getCreatedAtMs` extractor lets the
 * selector skip any candidate older than the latest user prompt so the
 * runway stays anchored on the in-flight turn.
 *
 * DO NOT REVERT to `findIndex` from the front of the array. Guarded by:
 * `CUR-206: anchors latest interleaved text segment as active response`
 * and `does not yank short turns to the bottom when optimistic +
 * persisted user rows briefly coexist post-stream`.
 */
export interface ActiveResponseSelectorOptions {
  readonly latestUserIndex?: number
  readonly latestUserCreatedAtMs?: number | null
  readonly getCreatedAtMs?: (message: ChatMessage) => number | null
}

export function getActiveResponseTextMessageIndex(
  messages: ChatMessage[],
  options: ActiveResponseSelectorOptions = {}
): number {
  const latestUserIndex = options.latestUserIndex ?? getLatestUserMessageIndex(messages)
  if (latestUserIndex < 0) return -1
  const latestUserCreatedAtMs = options.latestUserCreatedAtMs ?? null
  const getCreatedAtMs = options.getCreatedAtMs
  for (let i = messages.length - 1; i > latestUserIndex; i -= 1) {
    const message = messages[i]
    if (message.role !== 'assistant' || message.type !== 'text') continue
    if (latestUserCreatedAtMs !== null && getCreatedAtMs) {
      const messageCreatedAt = getCreatedAtMs(message)
      if (messageCreatedAt !== null && messageCreatedAt < latestUserCreatedAtMs) {
        continue
      }
    }
    return i
  }
  return -1
}

/**
 * Index of the LAST `progress_message` in the array, or -1 if none.
 *
 * Used by render code to determine which progress cluster is the "active"
 * one (the one that should pulse / show the shimmer). Pairs with
 * `hasUserMessageAfterLastProgress` below — a progress cluster is only
 * active if there is no user message after it (i.e., it belongs to the
 * latest turn).
 */
export function getLastProgressMessageIndex(messages: ChatMessage[]): number {
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    if (messages[i].type === 'progress_message') return i
  }
  return -1
}

/**
 * True iff a user message follows the last `progress_message` in the
 * array. If true, the last progress cluster belongs to a PREVIOUS turn
 * (the new user message starts a new turn) and MUST NOT be marked active.
 *
 * Optional `ignoreUserMessageIds` excludes out-of-order or prior-task-
 * continuation user rows that briefly co-exist with the current turn
 * during a post-stream silent refetch — without this guard a transient
 * out-of-order user row would deactivate the streaming progress cluster.
 *
 * Guarded by: progress clusters from old turns don't reactivate when the
 * user sends a new prompt; `does not deactivate the streaming progress
 * cluster when a prior persisted user row briefly co-exists`.
 */
export interface HasUserAfterProgressOptions {
  readonly ignoreUserMessageIds?: ReadonlySet<string>
}

export function hasUserMessageAfterLastProgress(
  messages: ChatMessage[],
  options: HasUserAfterProgressOptions = {}
): boolean {
  const lastProgressIndex = getLastProgressMessageIndex(messages)
  if (lastProgressIndex < 0) return false
  const ignore = options.ignoreUserMessageIds
  for (let i = lastProgressIndex + 1; i < messages.length; i += 1) {
    const message = messages[i]
    if (message.role !== 'user') continue
    if (ignore && ignore.has(message.id)) continue
    return true
  }
  return false
}

/**
 * Inclusive range of indices for the cluster (run of assistant entries)
 * starting at `start`. The cluster ends one before the next user message
 * (or at the end of the array). If `start` is itself a user message, the
 * cluster is empty (start..start-1).
 *
 * The chat surface treats every contiguous run of non-user messages
 * between two user messages as a "cluster" — feedback controls render
 * on the cluster's last entry, not on each entry. See
 * `computeFeedbackPositions` (chatTimeline.ts) for the original rule.
 */
export function getClusterRange(
  messages: ChatMessage[],
  start: number
): { startIndex: number; endIndex: number } {
  if (start < 0 || start >= messages.length || messages[start].role === 'user') {
    return { startIndex: start, endIndex: start - 1 }
  }
  let end = start
  while (end + 1 < messages.length && messages[end + 1].role !== 'user') {
    end += 1
  }
  return { startIndex: start, endIndex: end }
}

/**
 * All assistant TEXT messages in the same turn as `messageIndex`, in
 * array order. A turn is delimited by user messages on either side.
 *
 * CUR-206: with interleaved progress + text, a turn can contain multiple
 * assistant-text segments (think -> text -> think -> text). The TTS
 * button on the cluster's last entry plays back ALL of these — passing
 * back the whole list lets the caller concatenate text and detect
 * "is this segment the last of the turn?". See `isLastTextOfTurn`.
 *
 * Returns `[]` if `messageIndex` is out of bounds or not an assistant
 * message.
 */
export function getTurnTextSegments(messages: ChatMessage[], messageIndex: number): ChatMessage[] {
  if (messageIndex < 0 || messageIndex >= messages.length) return []
  const anchor = messages[messageIndex]
  if (anchor.role !== 'assistant') return []

  let turnStart = 0
  for (let i = messageIndex - 1; i >= 0; i -= 1) {
    if (messages[i].role === 'user') {
      turnStart = i + 1
      break
    }
  }
  let turnEnd = messages.length
  for (let i = messageIndex + 1; i < messages.length; i += 1) {
    if (messages[i].role === 'user') {
      turnEnd = i
      break
    }
  }
  const segments: ChatMessage[] = []
  for (let i = turnStart; i < turnEnd; i += 1) {
    const candidate = messages[i]
    if (candidate.role === 'assistant' && candidate.type === 'text') {
      segments.push(candidate)
    }
  }
  return segments
}

/**
 * True iff `message` is the last assistant TEXT segment of its turn
 * within `messages`. The TTS control gates on this — earlier segments
 * are mid-turn fragments and showing the button there strands it above
 * the next thinking bubble (CUR-206).
 *
 * DO NOT REVERT to "last assistant message of any type" — interleaved
 * turns that end in a PMV would then move the TTS control off the
 * last-text-segment unexpectedly. The cluster-end feedback row in the
 * PMV branch handles the PMV-tail case separately.
 */
export function isLastTextOfTurn(messages: ChatMessage[], messageIndex: number): boolean {
  if (messageIndex < 0 || messageIndex >= messages.length) return false
  const message = messages[messageIndex]
  if (message.role !== 'assistant' || message.type !== 'text') return false
  const segments = getTurnTextSegments(messages, messageIndex)
  if (segments.length === 0) return false
  return segments[segments.length - 1] === message
}

/**
 * Information about whether the latest turn is "interleaved" (CUR-206
 * shape: any text entry precedes any progress entry within the same
 * turn). When this is true, render code switches from the original
 * 3-slot layout (progress slot, response slot, supplementals) to
 * arrival-order rendering with the LAST progress and LAST text picked
 * as the active anchors.
 */
export interface InterleaveInfo {
  /** True if the latest turn has interleaved think -> text -> think -> text. */
  readonly isInterleaved: boolean
  /** Index in `messages` of the LAST progress_message in the turn, or -1. */
  readonly activeProgressIndex: number
  /** Index in `messages` of the LAST text message in the turn, or -1. */
  readonly activeResponseIndex: number
}

/**
 * Compute interleave info for a pre-filtered slice of assistant entries
 * in the latest turn (e.g. ``latestTurnAssistantEntries`` from
 * ChatDisplay, which already excludes prior-task continuations and
 * pre-timestamp messages). Prefer this over
 * :func:`detectInterleavedTurn` when the caller has already narrowed
 * the slice — passing the full ``processedMessages`` array can falsely
 * flip interleaved layout on stale continuation rows.
 */
export function detectInterleavedTurnFromEntries(
  entries: ReadonlyArray<{ message: ChatMessage; index: number }>
): InterleaveInfo {
  let sawText = false
  let isInterleaved = false
  let activeProgressIndex = -1
  let activeResponseIndex = -1
  for (const { message, index } of entries) {
    if (message.type === 'text') {
      sawText = true
      activeResponseIndex = index
    } else if (message.type === 'progress_message') {
      if (sawText) isInterleaved = true
      activeProgressIndex = index
    }
  }
  return { isInterleaved, activeProgressIndex, activeResponseIndex }
}

/**
 * Compute the interleave info for the latest turn. `messages` is the full
 * conversation array; the function locates the latest user message and
 * looks at the assistant entries after it.
 */
export function detectInterleavedTurn(messages: ChatMessage[]): InterleaveInfo {
  const latestUserIndex = getLatestUserMessageIndex(messages)
  const empty: InterleaveInfo = {
    isInterleaved: false,
    activeProgressIndex: -1,
    activeResponseIndex: -1
  }

  if (latestUserIndex < 0) {
    return empty
  }

  const entries: Array<{ message: ChatMessage; index: number }> = []
  for (let i = latestUserIndex + 1; i < messages.length; i += 1) {
    const message = messages[i]
    if (message.role !== 'assistant') continue
    entries.push({ message, index: i })
  }
  return detectInterleavedTurnFromEntries(entries)
}
